import { supabase } from '@/lib/supabase';

/** Stabile Namen für Auswertungen im Superuser-Dashboard */
export const AnalyticsEvent = {
  page_view: 'page_view',
  immobilien_list_view: 'immobilien_list_view',
  pakete_view: 'pakete_view',
  analyse_view: 'analyse_view',
  analyse_run: 'analyse_run',
  analyse_followup_run: 'analyse_followup_run',
  analyse_klaerung_run: 'analyse_klaerung_run',
  /** KI-Vorschlag bei einer Nutzer-Kontext-Frage per Klick übernommen */
  kontextfrage_vorschlag_uebernommen: 'kontextfrage_vorschlag_uebernommen',
  einstellungen_view: 'einstellungen_view',
  admin_analytics_view: 'admin_analytics_view',
  feedback_submitted: 'feedback_submitted',
  // Weitere Seiten-Views (Funnel-/Interesse-Tracking)
  vergleich_view: 'vergleich_view',
  renditerechner_view: 'renditerechner_view',
  doku_view: 'doku_view',
  // Kauf-Funnel
  checkout_started: 'checkout_started',
  checkout_completed: 'checkout_completed',
  checkout_cancelled: 'checkout_cancelled',
  // Renditerechner-Nutzung
  rendite_saved: 'rendite_saved',
  rendite_steuer_used: 'rendite_steuer_used',
  rendite_verlauf_view: 'rendite_verlauf_view',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

type QueueItem = {
  event_name: AnalyticsEventName;
  path: string | null;
  metadata: Record<string, unknown>;
};

const MAX_QUEUE = 40;
const FLUSH_MS = 1800;

let queue: QueueItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function postIngest(events: QueueItem[]) {
  if (!supabase || events.length === 0) return;
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token || token.startsWith('mock-local-dev')) return;

  await fetch('/api/analytics-ingest', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
  }).catch(() => {});
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_MS);
}

async function flushQueue() {
  if (!queue.length) return;
  const batch = queue.splice(0, 25);
  await postIngest(batch);
}

/**
 * Ein oder mehrere Nutzungs-Events (First-Party). Wird gebündelt an /api/analytics-ingest gesendet.
 */
export function trackAnalytics(
  event_name: AnalyticsEventName,
  opts?: { path?: string | null; metadata?: Record<string, unknown> }
) {
  if (queue.length >= MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE + 1);
  }
  const path =
    opts?.path != null
      ? String(opts.path).slice(0, 512)
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`.slice(0, 512)
        : null;
  const metadata =
    opts?.metadata && typeof opts.metadata === 'object' && !Array.isArray(opts.metadata)
      ? opts.metadata
      : {};
  queue.push({ event_name, path, metadata });
  scheduleFlush();
}

/** Vor Unmount / Navigation: Warteschlange sofort leeren */
export function flushAnalyticsNow() {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  void flushQueue();
}
